import {Advertisement as AdvertisementTypes} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/advertisement.types';
import {lazy} from '@aboutyou/lazy-imports';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {Promotion, TrackPromotions} from '@aboutyou/tracking';
import {
  BannerImage,
  InfoIconWithLink,
  UnstyledLink,
  mediaTabletAndUp,
  useGetAriaLabelFromUrl,
} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';
import {convertAdvertisementToTile} from '../../shared-deprecated/convertAdvertisementToTile';

const TrackBASMonitoring = lazy(() =>
  import('@aboutyou/tracking/src/trackerComponents/TrackBASMonitoring').then(
    mod => ({
      default: mod.TrackBASMonitoring,
    }),
  ),
);

export interface AdvertisementTracking {
  promotionSource:
    | TrackingPromotionCreative.BasAdpBanner
    | TrackingPromotionCreative.BasBasketBanner
    | TrackingPromotionCreative.BasWishlistBanner
    | TrackingPromotionCreative.BasAddToBasketFlyOut
    | TrackingPromotionCreative.BasOspBanner;
  streamPosition?: number;
}

interface AdvertisementProps {
  className?: string;
  testId?: string;
  contained?: boolean;
  tracking: AdvertisementTracking;
  slim?: boolean;
  advertisement?: AdvertisementTypes;
}

export const Advertisement: FC<AdvertisementProps> = ({
  className,
  testId = 'advertisement',
  contained,
  tracking,
  slim,
  advertisement,
}) => {
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();
  const {promotionSource, streamPosition = NaN} = tracking;

  if (!advertisement)
    return <TrackBASMonitoring basPromotionSource={promotionSource} />;

  const advertisementTile = convertAdvertisementToTile(advertisement);

  if (!advertisementTile)
    return <TrackBASMonitoring basPromotionSource={promotionSource} />;

  const image = advertisement.image;

  const {targetUrl, promotionTracking} = advertisementTile;

  const bannerImage = (
    <StyledBannerImage
      url={image.url}
      disableFixedRatio
      testId={`${testId}Banner`}
      $contained={contained}
    />
  );

  return (
    <StyledTrackPromotions
      $slim={slim}
      promotion={
        {
          advertisement: {
            ...(promotionTracking?.id
              ? {promotionId: promotionTracking.id}
              : {}),
            id: advertisementTile.id,
            path: targetUrl || '',
            name: promotionTracking?.name || image.description,
          },
          source: promotionSource,
        } as Promotion
      }
      streamPosition={streamPosition}
      data-testid={`${testId}Wrapper`}
      className={className}
      triggerOnce={true}
    >
      {targetUrl === undefined ? (
        bannerImage
      ) : (
        <UnstyledLink
          data-testid={`${testId}TargetLink`}
          to={targetUrl}
          aria-label={getAriaLabelFromUrl(targetUrl)}
        >
          {bannerImage}
        </UnstyledLink>
      )}
      {advertisement.infoIcon?.cta?.linkTarget.url && (
        <InfoIconWithLink
          to={advertisement.infoIcon.cta.linkTarget.url}
          aria-label={getAriaLabelFromUrl(
            advertisement.infoIcon.cta.linkTarget.url,
          )}
        />
      )}
    </StyledTrackPromotions>
  );
};

const StyledTrackPromotions = styled(TrackPromotions)<{$slim?: boolean}>`
  position: relative;
  aspect-ratio: 4 / 1;
  width: 100%;

  ${({$slim}) =>
    $slim &&
    css`
      aspect-ratio: 3.9 / 1; // The BAS team usually sets 750x192 images

      @media ${mediaTabletAndUp} {
        aspect-ratio: 9.5 / 1;
      }
    `};
`;

const StyledBannerImage = styled(BannerImage)<{$contained?: boolean}>`
  width: 100%;
  height: 100%;
  ${({$contained}) =>
    $contained &&
    css`
      background-size: contain;
      background-position: top;
    `};
`;
